// ───────────────────────────────────────────────────────────────
// store/slices/listSlice.ts
// ───────────────────────────────────────────────────────────────
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import useApi from '../../services/useApi';

/* ------------------------------------------------------------------ */
/*   Interfaces / Types                                              */
/* ------------------------------------------------------------------ */
export interface Row {
  _clientId?: string;             
  id?: string;
  ticket_id?: string;
  user_id?: string;
  user_type?: string;
  email?: string;
  reason?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  assignee_user_id?: string | null;
  assignee_user_type?: string | null; 
  province?: string | null;
  region?: string | null;
  district?: string | null;
  channel?: string | null;
  priority?: string | null;
  user_name?: string;
  user_code?: string;
  assignee_name?: string;
  assignee_user_code?: string;
  [key: string]: any;
}

interface ListState {
  loading: boolean;
  error: string | null;
  success: string | null;

  data: Row[];
  recordCount?: number;
  statusCode?: number;
  upfrontServiceFee?: number;
  entireResponse?: any;

  staticData: Row[];
}

export interface ApiResponse {
  statusCode: number | undefined;
  error: boolean;
  data: any;
}

interface ErrorPayload {
  message: string;
}

/* ------------------------------------------------------------------ */
/*   Helper Functions                                                */
/* ------------------------------------------------------------------ */
const generateId = () => `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ensureClientIds = (rows: Row[]): Row[] => {
  return rows.map(row => ({
    ...row,
    _clientId: row._clientId || generateId(),
  }));
};

/* ------------------------------------------------------------------ */
/*   Initial state                                                   */
/* ------------------------------------------------------------------ */
const initialState: ListState = {
  loading: true,
  error: null,
  success: null,

  data: [],
  recordCount: undefined,
  statusCode: undefined,
  upfrontServiceFee: undefined,
  entireResponse: undefined,

  staticData: [],
};

/* ------------------------------------------------------------------ */
/*   Async thunk – fetch list                                        */
/* ------------------------------------------------------------------ */
export const fetchList = createAsyncThunk<
  ApiResponse,
  string,
  { rejectValue: ErrorPayload }
>('list/fetch', async (url, { rejectWithValue }) => {
  try {
    const api = useApi();
    const apiRes = await api.getAPI(url);

    // Map admin support tickets response
    if (url.includes('admin-support')) {
      return {
        ...apiRes,
        statusCode: 200,
        data: {
          data: apiRes.data.tickets,
          total_count: apiRes.data.total_count,
          summary: apiRes.data.summary,
          max_upfront_service_fee_percent: apiRes.data.max_upfront_service_fee_percent,
        },
      };
    }

    return { ...apiRes, statusCode: apiRes.data.status };
  } catch (err: any) {
    console.error('Error fetching list:', err);
    return rejectWithValue({
      message: err?.message || 'Unknown error occurred',
    });
  }
});

/* ------------------------------------------------------------------ */
/*    Slice                                                           */
/* ------------------------------------------------------------------ */
const listSlice = createSlice({
  name: 'list',
  initialState,
  reducers: {
    resetState: () => initialState,

    // Replace entire staticData with client IDs
    setStaticData: (state, action: PayloadAction<Row[]>) => {
      state.staticData = ensureClientIds(action.payload);
    },

    // Update a single row by client ID
    updateRow: (state, action: PayloadAction<Row>) => {
      const idx = state.staticData.findIndex(r => r._clientId === action.payload._clientId);
      if (idx !== -1) {
        state.staticData[idx] = { 
          ...state.staticData[idx], 
          ...action.payload, 
          _clientId: state.staticData[idx]._clientId 
        };
      }
    },

    // Add a new row
    addRow: (state, action: PayloadAction<Row>) => {
      const newRow = { ...action.payload, _clientId: generateId() };
      state.staticData.push(newRow);
    },

    // Delete a row by client ID
    deleteRow: (state, action: PayloadAction<string>) => {
      state.staticData = state.staticData.filter(r => r._clientId !== action.payload);
    },

    // Update multiple rows by user_code
    updateRowsByUserCode: (
      state,
      action: PayloadAction<{ userCode: string; changes: Partial<Row> }>
    ) => {
      const { userCode, changes } = action.payload;
      state.staticData = state.staticData.map(row =>
        row.user_code === userCode ? { ...row, ...changes, _clientId: row._clientId } : row
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchList.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchList.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload.error) {
          state.data = ensureClientIds(action.payload.data.data);
          state.recordCount = action.payload.data.total_count;
          state.success = 'true';
          state.upfrontServiceFee = action.payload?.data.max_upfront_service_fee_percent;
          state.entireResponse = action.payload?.data;
        } else {
          state.success = 'false';
          state.statusCode = action.payload.statusCode;
          state.error = 'Error occurred while fetching list';
        }
      })
      .addCase(fetchList.rejected, (state, action) => {
        state.loading = false;
        state.data = [];
        state.error = action.payload?.message || 'Unknown error occurred';
        state.success = null;
      });
  },
});

/* ------------------------------------------------------------------ */
/*   Exports                                                         */
/* ------------------------------------------------------------------ */
export const {
  resetState,
  setStaticData,
  updateRow,
  addRow,
  deleteRow,
  updateRowsByUserCode,
} = listSlice.actions;

export default listSlice.reducer;
 